import React, { useRef, useState, useEffect, useCallback } from 'react';
import QrScanner from 'qr-scanner';
import Swal from 'sweetalert2';

const SimpleQRScanner = ({ onQRScanned, onClose }) => {
    const [isScannerOpen, setIsScannerOpen] = useState(false);
    const [isStarting, setIsStarting] = useState(false);
    const [error, setError] = useState(null);

    const videoRef = useRef(null);
    const scannerInstanceRef = useRef(null);

    const handleQrResult = useCallback((result) => {
        console.log('🎯 QR Code detected with qr-scanner!');
        console.log('📄 QR content:', result);

        try {
            // Validación básica
            if (!result || result.length < 1) {
                throw new Error('Código QR vacío');
            }

            let qrData;

            // Verificar si es una URL de ticket
            if (result.includes('/api/tickets/') && result.includes('/qr-accumulate')) {
                console.log('🔗 URL de ticket detectada:', result);

                // Extraer ticketId de la URL
                const urlParts = result.split('/');
                const ticketIdIndex = urlParts.findIndex(part => part === 'tickets') + 1;
                const ticketId = ticketIdIndex < urlParts.length ? urlParts[ticketIdIndex] : null;

                qrData = {
                    type: 'ticket_accumulation',
                    ticketId: ticketId,
                    url: result,
                    source: 'url',
                    rawText: result
                };
                console.log('✅ URL QR parsed:', qrData);
            } else {
                // Intentar parsear como JSON
                try {
                    qrData = JSON.parse(result);
                    qrData.source = 'json';
                    console.log('✅ JSON QR parsed:', qrData);
                } catch (parseError) {
                    // Si no es JSON, tratar como texto
                    qrData = {
                        type: 'ticket_accumulation',
                        ticketId: result,
                        source: 'text',
                        rawText: result
                    };
                    console.log('📝 QR as text:', qrData);
                }
            }

            // Cerrar scanner y procesar
            stopScanner();
            onQRScanned(qrData);

        } catch (error) {
            console.error('❌ Error processing QR code:', error);
            Swal.fire({
                title: 'QR Inválido',
                text: error.message || 'No se pudo procesar el código QR.',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
        }
    }, [onQRScanned]);

    const startScanner = useCallback(async () => {
        if (!videoRef.current) {
            console.error('❌ videoRef.current is null');
            setError('Elemento de video no encontrado');
            return;
        }

        console.log('🚀 Starting qr-scanner...');
        setIsStarting(true);
        setError(null);

        try {
            // Crear instancia del scanner
            const qrScanner = new QrScanner(
                videoRef.current,
                handleQrResult,
                {
                    highlightScanRegion: true,
                    highlightCodeOutline: true,
                    preferredCamera: 'environment', // Cámara trasera
                    maxScansPerSecond: 5,
                }
            );

            // Iniciar el scanner
            await qrScanner.start();
            
            console.log('✅ QR Scanner (qr-scanner) started successfully!');
            
            scannerInstanceRef.current = qrScanner;
            setIsScannerOpen(true);
            setIsStarting(false);

            // Forzar visualización del video después de un delay
            setTimeout(() => {
                if (videoRef.current) {
                    console.log('🔧 Forcing video display...');
                    const video = videoRef.current;
                    
                    // Aplicar estilos de forma más agresiva
                    video.style.setProperty('display', 'block', 'important');
                    video.style.setProperty('visibility', 'visible', 'important');
                    video.style.setProperty('opacity', '1', 'important');
                    video.style.setProperty('position', 'relative', 'important');
                    video.style.setProperty('z-index', '999', 'important');
                    video.style.setProperty('background-color', '#000', 'important');
                    video.style.setProperty('object-fit', 'cover', 'important');
                    
                    // Forzar el video a ser visible
                    video.removeAttribute('hidden');
                    video.removeAttribute('style');
                    video.style.cssText = `
                        width: 100% !important;
                        height: auto !important;
                        max-height: 400px !important;
                        border-radius: 8px !important;
                        display: block !important;
                        visibility: visible !important;
                        opacity: 1 !important;
                        position: relative !important;
                        z-index: 999 !important;
                        background-color: #000 !important;
                        object-fit: cover !important;
                    `;
                    
                    console.log('✅ Video display forced with aggressive styling');
                }
            }, 1000);

        } catch (error) {
            setIsStarting(false);
            console.error('Error starting qr-scanner:', error);

            let errorMessage = 'Error al iniciar la cámara';
            if (error.name === 'NotAllowedError') {
                errorMessage = 'Permisos de cámara denegados. Por favor, permite el acceso a la cámara.';
            } else if (error.name === 'NotFoundError') {
                errorMessage = 'No se encontró ninguna cámara disponible.';
            } else if (error.name === 'NotReadableError') {
                errorMessage = 'La cámara está siendo utilizada por otra aplicación.';
            }

            setError(errorMessage);

            Swal.fire({
                title: 'Error de Cámara',
                text: errorMessage,
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
        }
    }, [handleQrResult]);

    const stopScanner = useCallback(async () => {
        if (scannerInstanceRef.current) {
            try {
                scannerInstanceRef.current.stop();
                scannerInstanceRef.current.destroy();
                console.log('QR Scanner (qr-scanner) stopped successfully');
            } catch (error) {
                console.error('Error stopping qr-scanner:', error);
            }
            scannerInstanceRef.current = null;
        }
        setIsScannerOpen(false);
        setIsStarting(false);
    }, []);

    // Cleanup
    useEffect(() => {
        return () => {
            if (scannerInstanceRef.current) {
                try {
                    scannerInstanceRef.current.stop();
                    scannerInstanceRef.current.destroy();
                    console.log("QR Scanner (qr-scanner) stopped and destroyed.");
                } catch (err) {
                    console.log("Error stopping qr-scanner:", err);
                }
            }
        };
    }, []);

    return (
        <div className="w-full min-h-[300px] bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center relative">
            {!isScannerOpen && !isStarting && (
                <div className="text-center p-8">
                    <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                        Escáner de Código QR
                    </h3>
                    <p className="text-gray-600 mb-4">
                        Haz clic en "Iniciar Escáner" para comenzar a escanear códigos QR
                    </p>

                    <div className="space-x-4">
                        <button
                            onClick={startScanner}
                            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                        >
                            Iniciar Escáner
                        </button>
                        <button
                            onClick={() => {
                                // Test manual con URL de ticket
                                const testUrl = 'http://localhost:3001/api/tickets/68f101736d90092a27a59553/qr-accumulate';
                                console.log('🧪 Testing manual QR detection...');
                                handleQrResult(testUrl);
                            }}
                            className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
                        >
                            Test QR
                        </button>
                        <button
                            onClick={onClose}
                            className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded"
                        >
                            Cerrar
                        </button>
                    </div>
                </div>
            )}

            {isStarting && (
                <div className="text-center p-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Iniciando cámara...</p>
                </div>
            )}

            {/* Video element for qr-scanner */}
            <video
                ref={videoRef}
                style={{
                    width: "100%",
                    height: "auto",
                    maxHeight: "400px",
                    borderRadius: "8px",
                    display: isScannerOpen ? 'block !important' : 'none',
                    visibility: isScannerOpen ? 'visible !important' : 'hidden',
                    opacity: isScannerOpen ? '1 !important' : '0',
                    position: 'relative !important',
                    zIndex: '999 !important',
                    backgroundColor: '#000',
                    objectFit: 'cover'
                }}
                playsInline
                muted
                autoPlay
                controls={false}
            />

            {isScannerOpen && (
                <>
                    <div className="absolute top-4 right-4 z-10">
                        <button
                            onClick={stopScanner}
                            className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
                        >
                            Detener
                        </button>
                    </div>

                    <div className="absolute top-4 left-4 z-10 space-y-2">
                        <button
                            onClick={() => {
                                console.log('🔍 Debug info:');
                                console.log('Scanner instance:', scannerInstanceRef.current);
                                console.log('Video element:', videoRef.current);
                                console.log('Video dimensions:', {
                                    width: videoRef.current?.videoWidth,
                                    height: videoRef.current?.videoHeight,
                                    clientWidth: videoRef.current?.clientWidth,
                                    clientHeight: videoRef.current?.clientHeight
                                });
                                console.log('Video computed styles:', videoRef.current ? window.getComputedStyle(videoRef.current) : 'No video element');
                                console.log('Video attributes:', videoRef.current ? Array.from(videoRef.current.attributes).map(attr => `${attr.name}="${attr.value}"`) : 'No video element');
                            }}
                            className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded text-sm block w-full"
                        >
                            Debug
                        </button>
                        <button
                            onClick={() => {
                                if (videoRef.current) {
                                    console.log('🔧 Forcing video display manually...');
                                    const video = videoRef.current;
                                    
                                    // Aplicar estilos de forma más agresiva
                                    video.style.setProperty('display', 'block', 'important');
                                    video.style.setProperty('visibility', 'visible', 'important');
                                    video.style.setProperty('opacity', '1', 'important');
                                    video.style.setProperty('position', 'relative', 'important');
                                    video.style.setProperty('z-index', '999', 'important');
                                    video.style.setProperty('background-color', '#000', 'important');
                                    video.style.setProperty('object-fit', 'cover', 'important');
                                    
                                    // Forzar el video a ser visible
                                    video.removeAttribute('hidden');
                                    video.removeAttribute('style');
                                    video.style.cssText = `
                                        width: 100% !important;
                                        height: auto !important;
                                        max-height: 400px !important;
                                        border-radius: 8px !important;
                                        display: block !important;
                                        visibility: visible !important;
                                        opacity: 1 !important;
                                        position: relative !important;
                                        z-index: 999 !important;
                                        background-color: #000 !important;
                                        object-fit: cover !important;
                                    `;
                                    
                                    console.log('✅ Video display forced manually with aggressive styling');
                                }
                            }}
                            className="bg-orange-600 hover:bg-orange-700 text-white font-bold py-2 px-4 rounded text-sm block w-full"
                        >
                            Mostrar Video
                        </button>
                    </div>

                    <div className="absolute bottom-4 left-4 bg-green-500 text-white px-3 py-1 rounded-full text-sm flex items-center space-x-2">
                        <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                        <span>Escaneando QR (qr-scanner)...</span>
                    </div>
                </>
            )}

            {error && (
                <div className="absolute inset-0 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center justify-center">
                    <p>{error}</p>
                </div>
            )}
        </div>
    );
};

export default SimpleQRScanner;